import React, { useContext, useState } from "react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Card, CardActions, CardContent, IconButton, Popover, Stack, Typography, Snackbar, Alert } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PasswordIcon from "@mui/icons-material/Password";
import { ActionIconStyle } from "../utils/AAStyles";
import { useTheme } from "@mui/material/styles";
import { UserContext } from "../context/userContext";
import { adsBlueA, adsMidGreyA, adsWhite } from "../utils/ADSColours";
import ResetPasswordDialog from "../dialogs/user/ResetPasswordDialog";
import { UpdateUserPassword } from "../configuration/AAConfig_Security";
import LoginDialog from "../dialogs/LoginDialog";

const AAUserAvatar: React.FC = () => {
  const { logoff, currentUser, currentToken, updateShowResetPasswordDialog } = useContext(UserContext);
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const settingsOpen = Boolean(anchorEl);
  const settingsPopoverId = settingsOpen ? "ads-settings-popover" : undefined;

  const [showResetPassword, setShowResetPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleSettingsClose = () => {
    setAnchorEl(null);
  };

  const getActionStyle = () => ({
    backgroundColor: adsWhite,
    color: adsMidGreyA,
    "&:hover": {
      color: adsBlueA,
    },
  });

  const handleAvatarClick = () => {
    if (currentUser) {
      setAnchorEl(document.getElementById("imanage-user-settings") as HTMLButtonElement);
    }
  };

  const handleResetPasswordClick = () => {
    updateShowResetPasswordDialog(true);
  };

  const updatePassword = async (newPassword: string) => {
    if (!currentUser) return;
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const errors = await UpdateUserPassword(currentToken, currentUser.id, newPassword);
      if (!errors) {
        setSuccessMsg("Password has been successfully reset.");
        setShowResetPassword(false);
      } else {
        setErrorMsg(errors);
      }
    } catch (error) {
      setErrorMsg("Failed to reset password. Please try again.");
    }
  };

  return (
    <>
      <IconButton
        id="imanage-user-settings"
        aria-label="profile"
        onClick={handleAvatarClick}
        size="large"
        sx={{ position: "absolute", bottom: 20 }}
      >
        <AccountCircleIcon fontSize="large" sx={ActionIconStyle()} />
      </IconButton>

      <Popover
        id={settingsPopoverId}
        open={settingsOpen}
        anchorEl={anchorEl}
        onClose={handleSettingsClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack direction="row" spacing={0.5}>
          {currentUser && (
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  {currentUser && (
                    <Typography sx={{ fontWeight: "bold" }}>
                      {`${currentUser.firstName} ${currentUser.lastName}`}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
              <CardActions>
                <Stack
                  sx={{ ml: theme.spacing(2), mr: theme.spacing(2), mb: theme.spacing(2) }}
                  direction="column"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <IconButton
                    sx={getActionStyle()}
                    id="ads-action-button-logout"
                    size="small"
                    onClick={() => logoff(true)}
                  >
                    <LogoutIcon />
                    <Typography variant="body2" sx={{ pl: theme.spacing(0.5), pr: theme.spacing(1) }}>
                      Log Out
                    </Typography>
                  </IconButton>

                  <IconButton
                    sx={getActionStyle()}
                    id="ads-action-button-change-password"
                    size="small"
                    onClick={handleResetPasswordClick}
                  >
                    <PasswordIcon />
                    <Typography variant="body2" sx={{ pl: theme.spacing(0.5), pr: theme.spacing(1) }}>
                      Change Password
                    </Typography>
                  </IconButton>
                </Stack>
              </CardActions>
            </Card>
          )}
        </Stack>
      </Popover>

      {showResetPassword && (
        <ResetPasswordDialog
          closeDialogHandler={() => setShowResetPassword(false)}
          continueHandler={updatePassword}
          continueError={errorMsg}
          backHandler={undefined}
        />
      )}

      {true && (
        <>
        <LoginDialog
          title="Idox ADS Security Login"
          message="Please enter your username and password."
          version={process.env.REACT_APP_SECURITY_VERSION || "1"}
          />
          </>
      )}

      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg(null)}>
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>

      <Snackbar open={!!successMsg} autoHideDuration={4000} onClose={() => setSuccessMsg(null)}>
        <Alert severity="success">{successMsg}</Alert>
      </Snackbar>
    </>
  );
};

export default AAUserAvatar;
